import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "foodgo_cookie_consent_v1";

export function getCookieConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
}

export default function CookieBanner() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: false });

  useEffect(() => {
    const c = getCookieConsent();
    if (!c) setVisible(true);
  }, []);

  const save = (consent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, ts: new Date().toISOString() }));
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto card-neon p-5 sm:p-6 backdrop-blur-md bg-deep-card/95 glow-violet">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-11 h-11 rounded-xl bg-deep-surface border border-line items-center justify-center shrink-0">
            <Cookie className="w-5 h-5 text-neon-yellow" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-base">{t.cookies.bannerTitle}</div>
            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
              {t.cookies.bannerText}{" "}
              <Link to="/privacy" className="text-neon-cyan hover:underline">{t.cookies.privacyLink}</Link> ·{" "}
              <Link to="/cookies" className="text-neon-cyan hover:underline">{t.cookies.cookieLink}</Link>
            </p>

            {advanced && (
              <div className="mt-4 space-y-2 text-xs">
                <PrefRow label={t.cookies.essentialLabel} desc={t.cookies.essentialDesc} value={true} disabled />
                <PrefRow label={t.cookies.analyticsLabel} desc={t.cookies.analyticsDesc} value={prefs.analytics} onChange={(v) => setPrefs({ ...prefs, analytics: v })} testId="cookie-pref-analytics" />
                <PrefRow label={t.cookies.marketingLabel} desc={t.cookies.marketingDesc} value={prefs.marketing} onChange={(v) => setPrefs({ ...prefs, marketing: v })} testId="cookie-pref-marketing" />
              </div>
            )}

            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => save({ essential: true, analytics: true, marketing: true, accepted: "all" })}
                className="btn-primary !py-2 text-xs"
                data-testid="cookie-accept-all"
              >
                {t.cookies.acceptAll}
              </button>
              <button
                onClick={() => save({ essential: true, analytics: false, marketing: false, accepted: "essential" })}
                className="btn-outline text-xs"
                data-testid="cookie-reject"
              >
                {t.cookies.rejectAll}
              </button>
              {!advanced ? (
                <button onClick={() => setAdvanced(true)} className="btn-ghost text-xs" data-testid="cookie-customize">
                  {t.cookies.customize}
                </button>
              ) : (
                <button
                  onClick={() => save({ essential: true, analytics: prefs.analytics, marketing: prefs.marketing, accepted: "custom" })}
                  className="btn-outline text-xs"
                  data-testid="cookie-save-prefs"
                >
                  {t.cookies.savePrefs}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ label, desc, value, onChange, disabled, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 border border-line rounded-xl px-3 py-2 bg-deep-surface">
      <div className="min-w-0">
        <div className="font-semibold text-zinc-200 text-xs">{label}</div>
        <div className="text-[10px] text-zinc-500 font-mono">{desc}</div>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange && onChange(!value)}
        className={`relative w-10 h-5 rounded-full transition shrink-0 ${value ? "bg-neon-cyan" : "bg-zinc-700"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        data-testid={testId}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
    </div>
  );
}
